import { computePeaks } from '../audio/analysis';
import { audioCtx } from '../audio/context';
import { putSample } from '../audio/registry';
import { encodeWav } from '../audio/wav';
import { getSample } from '../audio/registry';
import type { Project } from '../types';

/**
 * Project files (`.chop`) are JSON with every source's audio embedded as a
 * base64 16-bit WAV. Self-contained beats small — you can hand someone a file
 * and they hear the same thing.
 */

const MAGIC = 'happychopper-project';
/** Files written before the rename. Still perfectly valid. */
const LEGACY_MAGIC = 'wavchop-project';

interface Bundle {
  magic: string;
  version: number;
  project: Project;
  audio: Record<string, string>;
}

function toBase64(bytes: Uint8Array): string {
  let s = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(s);
}

function fromBase64(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export async function serializeProject(project: Project): Promise<Blob> {
  const audio: Record<string, string> = {};
  for (const src of project.sources) {
    const entry = getSample(src.id);
    if (!entry) continue;
    const wav = encodeWav(entry.buffer, 16);
    audio[src.id] = toBase64(new Uint8Array(await wav.arrayBuffer()));
  }
  const bundle: Bundle = { magic: MAGIC, version: 1, project, audio };
  return new Blob([JSON.stringify(bundle)], { type: 'application/json' });
}

export async function deserializeProject(file: File): Promise<Project> {
  const text = await file.text();
  const bundle = JSON.parse(text) as Bundle;
  if (bundle.magic !== MAGIC && bundle.magic !== LEGACY_MAGIC) {
    throw new Error('Not a Happy Chopper project file');
  }
  const ctx = audioCtx();
  for (const [id, b64] of Object.entries(bundle.audio ?? {})) {
    const bytes = fromBase64(b64);
    // decodeAudioData detaches the buffer, so hand it a private copy.
    const buffer = await ctx.decodeAudioData(bytes.buffer.slice(0) as ArrayBuffer);
    putSample(id, { buffer, peaks: computePeaks(buffer) });
  }
  return bundle.project;
}
